import {
  emailSchema,
  getMobilePhonePhSchema,
} from '@breezeventures/web-ph-validators/zod-schemas';
import { z } from 'zod';

const preprocessNumberField = <
  T extends z.ZodCoercedNumber | z.ZodOptional<z.ZodCoercedNumber>,
>(
  schema: T
) =>
  z.preprocess(
    (val) =>
      val === '' ||
      val === null ||
      (typeof val === 'string' && val.trim() === '')
        ? undefined
        : val,
    schema
  );

const baseStoreSchema = z.object({
  uiId: z.string(),
  name: z.string().trim().min(1, 'Store name is required'),
  email: emailSchema,
  contactNumber: getMobilePhonePhSchema({
    required: true,
    requiredMessage: 'Contact number is required',
  }),
  avgMonthlySales: preprocessNumberField(
    z.coerce.number('Enter a valid value').positive('Enter a valid value')
  ),
});

const onlineStoreSchema = baseStoreSchema.extend({
  type: z.literal('ONLINE'),
  websiteURL: z
    .string()
    .trim()
    .min(1, 'Website or social media page is required')
    .pipe(z.url('Enter valid URL')),
});

const offlineStoreSchema = baseStoreSchema
  .extend({
    type: z.literal('OFFLINE'),
    websiteURL: z.url('Enter valid URL').optional().or(z.literal('')),
    tinCode: z
      .string()
      .trim()
      .min(1, 'TIN is required')
      .regex(/^\d{9,13}$/, 'TIN must have 9-13 digits'),
    areaSqm: preprocessNumberField(
      z.coerce
        .number('Enter a valid store size')
        .positive('Enter a valid store size')
    ),
    numberOfFloors: preprocessNumberField(
      z.coerce
        .number('Enter a valid number of floors')
        .int('Number of floors must be an integer')
        .min(1, 'Number of floors must be at least 1')
    ),
    hasCCTV: z.boolean(),
    dayOfRecording: preprocessNumberField(
      z.coerce
        .number('Enter valid days number')
        .min(1, 'Days number must be positive')
        .optional()
    ),
  })
  .refine((data) => !data.hasCCTV || data.dayOfRecording !== undefined, {
    path: ['dayOfRecording'],
    message: 'Days of recording is required',
  });

export const storeDetailsSchema = z.discriminatedUnion('type', [
  onlineStoreSchema,
  offlineStoreSchema,
]);

export type StoreDetailsFormInput = z.input<typeof storeDetailsSchema>;
export type StoreDetailsFormValues = z.output<typeof storeDetailsSchema>;
export type OnlineStoreFormValues = z.infer<typeof onlineStoreSchema>;
export type OfflineStoreFormValues = z.infer<typeof offlineStoreSchema>;
